import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useMediaQuery } from '@mui/material';

import Modal from '../../Modal';
import ActionButton from '../../ActionButton';
import InputNew from '../../InputNew';
import ErrorForm from '../../ErrorForm';
import MultilineInput from '../components/MessageInput';
import BatchMessageVariables from './components/Variables';
import BatchMessageTemplates from './components/Templates';
import BatchMessageAttachments from './components/Attachments';
import BatchMessageTemplatePopup from '../BatchMessageTemplatePopup';
import {
  addBatchMessageStatus,
  closeBatchMessageComposer,
  setBatchMessageComposerMessage
} from '../../../sagas/batchMessage/actions';
import { batchMessageStatusItem } from '../../../sagas/batchMessage/reducers';
import { sendSendbirdBatchMessageRequest } from '../../../sagas/sendbird/batchMessage/send/actions';
import { getBatchMessageVariablesRequest } from '../../../sagas/sendbird/variables/actions';
import { convertJSONToFormData } from '../../../utils/custom';

import './_batchMessageComposer.scss';

const BatchMessageComposer = () => {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery('(min-width:577px)');
  const inputRef = useRef(null);
  const attachmentsRef = useRef(null);
  const [hasFocused, setHasFocused] = useState(false);
  const { composer: { open, name, users, message } } = useSelector(s => s.batchMessage);
  const { dataTable: variables, curr_page } = useSelector(s => s.getBatchMessageVariables);
  const { loading, errors_api } = useSelector(s => s.sendSendbirdBatchMessage);

  useEffect(() => {
    if (open) {
      dispatch(getBatchMessageVariablesRequest({ curr_page }));
    }
  }, [open]);

  const allowedVars = useMemo(() => new Set(variables.map(v => v.key)), [variables]);

  const onClose = () => {
    setHasFocused(false);
    attachmentsRef.current?.reset();
    dispatch(closeBatchMessageComposer());
  };

  const setMessage = value => dispatch(setBatchMessageComposerMessage({ message: value }));

  const applyVariable = value => {
    if (!allowedVars.has(value)) return;

    if (!hasFocused) {
      setMessage(`${message}${value}`);
      return;
    }

    const el = inputRef.current;
    if (!el) {
      setMessage(`${message}${value}`);
      return;
    }

    const start =
      typeof el.selectionStart === 'number' ? el.selectionStart : message.length;
    const end =
      typeof el.selectionEnd === 'number' ? el.selectionEnd : message.length;

    const nextMessage = message.slice(0, start) + value + message.slice(end);
    setMessage(nextMessage);

    requestAnimationFrame(() => {
      el.focus();
      const pos = start + value.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const renderName = () => {
    if (!name) {
      const usersDisplay = isDesktop ? 3 : 2;
      const usersLength = users.length > usersDisplay ? users.length - usersDisplay : 0;

      return `${users
        .slice(0, usersDisplay)
        .map(user => user.name)
        .join(', ')}${usersLength ? `, +${usersLength} more` : ''}`;
    }

    return name;
  };

  const insertTemplate = template => setMessage(template);

  const sendMessage = () => {
    const files = attachmentsRef.current?.getFiles() || [];
    let values;
    let multipart = false;

    if (files.length > 0) {
      const formData = convertJSONToFormData({
        message,
        user_ids: users.map(user => user.uuid)
      });
      files.forEach(file => formData.append('attachments', file));
      values = formData;
      multipart = true;
    } else {
      values = {
        user_ids: users.map(user => user.uuid),
        message
      };
    }

    dispatch(sendSendbirdBatchMessageRequest({
      values,
      multipart,
      handledErrors: ['message'],
      onSuccess: data => {
        dispatch(addBatchMessageStatus({
          ...batchMessageStatusItem,
          id: data.task_id,
          status: data.status
        }));
        onClose();
      }
    }));
  };

  return (
    <>
      <Modal open={open} closeModal={onClose} className="batchMessageComposer-popup">
        <div className="batchMessageComposer-header">
          <p className="batchMessageComposer-title">Send message</p>
        </div>

        <div className="batchMessageComposer-container">
          <div className="batchMessageComposer-content">
            <InputNew label="Recipients:" className="batchMessageComposer-name" value={renderName()} disabled/>

            <BatchMessageVariables variables={variables} applyVariable={applyVariable} />

            <div className="batchMessageComposer-field">
              <MultilineInput
                ref={inputRef}
                value={message}
                onChange={e => setMessage(e.target.value)}
                onFocus={() => setHasFocused(true)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '263px',
                    boxSizing: 'border-box'
                  },
                  '& textarea': {
                    height: '100% !important',
                    overflow: 'auto !important',
                    resize: 'none'
                  }
                }}
              />
            </div>

            <BatchMessageAttachments ref={attachmentsRef} />

            {errors_api.message &&  <ErrorForm message={errors_api.message} />}
          </div>
          <div className="batchMessageComposer-templates">
            <BatchMessageTemplates insertTemplate={insertTemplate}/>
            <div className="batchMessageComposer-buttons">
              <ActionButton disabled={isEmpty(users)} title="Send" loading={loading} onClick={sendMessage}/>
            </div>
          </div>
        </div>
      </Modal>
      <BatchMessageTemplatePopup variables={variables} />
    </>
  );
};

export default BatchMessageComposer;
